/**
 * Collection grid — the section's re-render controller.
 *
 * Owns every fetch and every content swap on the collection page. Four things
 * change the results, and all four come back as the same rendered section and
 * differ only in which regions get replaced:
 *
 *   apply filters   the tray's form is submitted
 *   remove one      a chip, or Clear all
 *   load more       the next page, APPENDED rather than replaced
 *   back / forward  the browser restoring a URL we pushed
 *
 * WHY ONE COMPONENT AND NOT TWO. Splitting this would mean the tray fetching a
 * grid it does not own, or the grid listening for a form outside itself. The host
 * wraps the whole section instead, so a single delegated listener sees the form,
 * the chips and the Load more link alike — and assets/filter-drawer.js stays a
 * disclosure that knows nothing about the network.
 *
 * THE TRAY'S OWN CONTROLS ARE NEVER SWAPPED. Only `[data-filter-columns]` inside
 * it is replaced — never the tray host, its control row or its form element — so
 * <filter-drawer>'s references survive every swap and it is never re-upgraded.
 *
 * WHY THE APPENDED ITEMS LAND IN THE RIGHT PLACE WITH NO WORK HERE. The editorial
 * photographs are placed at definite grid rows and columns by Liquid; every
 * product is auto-placed. CSS grid re-runs auto-placement over the whole grid
 * whenever items are added, so appended products flow around photographs that are
 * already positioned, and this file never computes a position.
 *
 * EVERY ENTRY POINT IS A REAL LINK OR A REAL FORM, AND THAT IS THE FALLBACK.
 * Without this script the form GETs, the chips navigate and Load more follows its
 * href — filtering and paging both still work, just with a page load. Any failure
 * here falls back to exactly that.
 *
 * PLURALISATION STAYS IN LIQUID. Both announcements come from a <template> inside
 * the INCOMING section, already rendered through the `one` / `other` forms.
 * JavaScript has no way to choose between them and never builds the string.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: section JS was
 * measured NOT running at all on this store.
 */
if (!customElements.get('collection-grid')) {
  customElements.define(
    'collection-grid',
    class CollectionGrid extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-product-grid]');
        this.status = this.querySelector('[data-grid-status]');

        /**
         * No grid is not a failure worth shouting about — but there is nothing to
         * control either, and every handler below assumes it.
         */
        if (!this.grid) return;

        this.busy = false;

        this.handleClick = this.handleClick.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handlePopState = this.handlePopState.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('submit', this.handleSubmit);

        /**
         * Back and forward have to work, because we push a URL for every filter
         * change. Without this the address bar would move and the products would
         * not — the single worst failure mode of AJAX filtering.
         */
        window.addEventListener('popstate', this.handlePopState);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('submit', this.handleSubmit);
        window.removeEventListener('popstate', this.handlePopState);

        /** A response landing on a detached element would write nowhere visible. */
        if (this.controller) this.controller.abort();
      }

      /* ------------------------------------------------------------------ */
      /* Entry points                                                        */
      /* ------------------------------------------------------------------ */

      handleClick(event) {
        if (!(event.target instanceof Element)) return;

        /** Let modified clicks through, so any of these still open in a new tab. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        const loadMore = event.target.closest('[data-load-more]');
        if (loadMore && this.contains(loadMore)) {
          event.preventDefault();
          if (!this.busy) this.loadNext(loadMore);
          return;
        }

        /** A chip's remove link, or Clear all — both plain links to a filtered URL. */
        const filterLink = event.target.closest('[data-filter-link]');
        if (filterLink && this.contains(filterLink)) {
          event.preventDefault();
          if (!this.busy) this.applyUrl(filterLink.getAttribute('href'));
        }
      }

      handleSubmit(event) {
        const form = event.target.closest('[data-filter-form]');
        if (!form || !this.contains(form)) return;
        if (this.busy) {
          event.preventDefault();
          return;
        }

        /**
         * Built from the form rather than from `location`, so an unticked box
         * genuinely disappears from the URL. Empty values are dropped: a blank
         * price field must not become `filter.v.price.gte=`, which Shopify reads
         * as a filter set to nothing rather than as no filter at all.
         *
         * NO `new URL()` HERE, AND THAT IS NOT A STYLE CHOICE. It needs a base,
         * and `new URL(action, location.origin)` throws `Invalid base URL`
         * wherever origin is the string "null" — any sandboxed or about:blank
         * document. It threw AFTER preventDefault, so the button did nothing at
         * all: no request, and no fallback navigation either. URLSearchParams
         * needs no base and cannot fail this way.
         */
        const action = form.getAttribute('action') || window.location.pathname;
        const params = new URLSearchParams();
        new FormData(form).forEach((value, key) => {
          if (String(value).trim() !== '') params.append(key, value);
        });
        const query = params.toString();

        /** Only now, once there is definitely a URL to go to. */
        event.preventDefault();
        this.applyUrl(query ? action + '?' + query : action);
      }

      handlePopState() {
        /** Never push what the browser has already put in the address bar. */
        this.applyUrl(window.location.pathname + window.location.search, { push: false });
      }

      /* ------------------------------------------------------------------ */
      /* Applying a filtered URL                                             */
      /* ------------------------------------------------------------------ */

      async applyUrl(href, { push = true } = {}) {
        if (!href) return;

        this.setBusy(true);

        try {
          const incoming = await this.fetchSection(href);

          this.replaceRegion(incoming, '[data-filter-columns]');
          this.replaceRegion(incoming, '[data-filter-chips]');
          this.replaceRegion(incoming, '[data-product-grid]');
          this.replaceRegion(incoming, '[data-load-more-region]');

          this.announce(incoming, '[data-results-label]');

          /**
           * The tray closes on apply, which is what makes the result visible. It
           * is closed AFTER the swap, not before, so the columns behind it are
           * already the new ones when it rolls up.
           */
          document.querySelector('filter-drawer')?.close?.();

          this.keepGridInView();

          /**
           * HISTORY IS UPDATED LAST AND CANNOT FAIL THE RENDER. `pushState`
           * throws on any document whose origin is opaque — a sandboxed frame,
           * about:blank — and inside the try above that turned a swap which had
           * ALREADY SUCCEEDED into a caught failure that navigated away and threw
           * the new content out. The products are the point; the address bar is
           * an enhancement on top of them.
           */
          if (push) {
            try {
              window.history.pushState({ filtered: true }, '', href);
            } catch (historyError) {
              console.warn('[collection-grid] could not update history', historyError);
            }
          }
        } catch (error) {
          if (error.name === 'AbortError') return;

          /**
           * Navigate for real rather than leaving the page showing results that
           * do not match the controls. This is the same thing that would have
           * happened with no JavaScript at all.
           */
          console.warn(error);
          window.location.assign(href);
        } finally {
          this.setBusy(false);
        }
      }

      /**
       * Replace one region's contents from the incoming document. Contents, never
       * the element itself — a replaced element loses the attributes and the
       * identity that the next swap and the CSS both depend on.
       */
      replaceRegion(incoming, selector) {
        const fresh = incoming.querySelector(selector);
        const current = this.querySelector(selector);
        if (!fresh || !current) return;
        current.innerHTML = fresh.innerHTML;
      }

      /**
       * After a filter the grid can become far shorter than it was. Someone
       * scrolled deep into 200 results who filters down to three would otherwise
       * be left looking at empty space with no idea anything happened.
       */
      keepGridInView() {
        const top = this.grid.getBoundingClientRect().top;
        if (top >= 0) return;

        const y = top + window.scrollY - 100;
        if (window.themeScroll) window.themeScroll.scrollTo(y);
        else window.scrollTo({ top: y, behavior: 'smooth' });
      }

      /* ------------------------------------------------------------------ */
      /* Loading the next page                                               */
      /* ------------------------------------------------------------------ */

      async loadNext(button) {
        const href = button.getAttribute('href');
        if (!href) return;

        this.busy = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const incoming = await this.fetchSection(href);
          const incomingGrid = incoming.querySelector('[data-product-grid]');
          if (!incomingGrid) throw new Error('[collection-grid] no grid in response');

          this.append(incomingGrid, incoming, button);
        } catch (error) {
          if (error.name === 'AbortError') return;

          console.warn(error);
          window.location.assign(href);
        } finally {
          this.busy = false;
          this.controller = null;
          if (button.isConnected) button.removeAttribute('aria-busy');
        }
      }

      /**
       * @param {Element} incomingGrid - The next page's grid
       * @param {Document} incoming - The parsed section, for the button and label
       * @param {Element} button - The Load more link that was clicked
       */
      append(incomingGrid, incoming, button) {
        const added = Array.from(incomingGrid.children);
        if (added.length === 0) return;

        const fragment = document.createDocumentFragment();
        added.forEach((node) => fragment.appendChild(node));
        this.grid.appendChild(fragment);

        this.announce(incoming, '[data-loaded-label]');

        const nextButton = incoming.querySelector('[data-load-more]');
        if (nextButton) {
          button.setAttribute('href', nextButton.getAttribute('href'));
          return;
        }

        /**
         * Last page. The button is removed, so focus has to go somewhere
         * deliberate — leaving it on a detached element sends it back to <body>
         * and a keyboard user loses their place entirely. The last appended card
         * is the nearest thing to where they were.
         */
        const wrapper = button.parentElement;
        const target = added[added.length - 1];
        const focusable = target.matches('a[href]') ? target : target.querySelector('a[href]');

        button.remove();
        if (wrapper && wrapper.children.length === 0) wrapper.remove();

        /**
         * No tabindex is added: the card is an `<a href>` and is already in the
         * tab sequence. Setting `tabindex="-1"` to make it focusable would take
         * it OUT of that sequence, which is the opposite of what is wanted here.
         */
        if (focusable) focusable.focus();
      }

      /* ------------------------------------------------------------------ */
      /* Shared                                                              */
      /* ------------------------------------------------------------------ */

      /**
       * @param {string} href - A collection URL, with or without filter params
       * @returns {Promise<Document>} The rendered section, parsed
       */
      async fetchSection(href) {
        this.controller = new AbortController();

        const separator = href.indexOf('?') === -1 ? '?' : '&';
        const url = href + separator + 'section_id=' + encodeURIComponent(this.dataset.sectionId);

        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('[collection-grid] ' + response.status);

        const markup = await response.text();
        return new DOMParser().parseFromString(markup, 'text/html');
      }

      /** Read a pre-rendered, already-pluralised string out of the response. */
      announce(incoming, selector) {
        const label = incoming.querySelector(selector);
        if (this.status && label) this.status.textContent = label.content.textContent.trim();
      }

      setBusy(next) {
        this.busy = next;
        this.grid.toggleAttribute('data-busy', next);
        this.grid.setAttribute('aria-busy', String(next));
      }
    }
  );
}
